/**
 * Schweizer Hochdeutsch — the shipped locale and the source of truth for the
 * key set. Rules that hold for every string in here:
 *   · never "ß", always "ss" (a test enforces it)
 *   · no dialect
 *   · no approval / verdict / score language — der Baueingabe-Check markiert, er entscheidet
 *     nicht (a test enforces the forbidden vocabulary too)
 *   · domain terms stay German in every locale (Baumassenziffer, Ausnützungs-
 *     ziffer, Nachweis, Baueingabe, Gemeinde, Zone, BZO)
 */
export const de = {
  'app.name': 'Baueingabe-Check-KAi',
  'app.attribution': 'von Velox-KAi',
  'app.tagline': 'Pre-Check für Baueingabe-Dossiers · Kanton Zürich',

  'nav.dossiers': 'Dossiers',
  'nav.report': 'Bericht',
  'nav.run': 'Prüflauf',

  'lang.label': 'Sprache',
  'lang.de': 'Deutsch (CH)',
  'lang.en': 'English',
  'lang.fr': 'Français',
  'lang.frPending': 'ausstehend',
  'lang.frBanner':
    'Die französische Oberfläche ist noch nicht übersetzt. Angezeigt wird Deutsch (CH).',

  'a11y.skipToContent': 'Zum Inhalt springen',

  // Ein Titel pro Route (WCAG 2.4.2). Kurz, weil ein Tab-Streifen rechts
  // abschneidet — die Ansicht zuerst, das Produkt danach.
  'title.home': 'Übersicht',
  'title.signIn': 'Anmelden',
  'title.invite': 'Einladung',
  'title.workspaceIndex': 'Workspaces',
  'title.workspace': 'Workspace',
  'title.upload': 'Dossier hochladen',
  'title.facts': 'Fakten prüfen',
  'title.dossierAudit': 'Verlauf des Dossiers',
  'title.admin': 'Verwaltung',
  'title.rulebase': 'Gemeinden und Regelwerk',
  'title.rulebaseIngest': 'BZO hochladen',
  'title.rulebaseIngestion': 'BZO-Erfassung',
  'title.rulebasePack': 'Entwurf verifizieren',
  'title.run': 'Prüflauf',
  'title.report': 'Bericht',
  'title.reportPrint': 'Bericht — Druckfassung',
  'title.notFound': 'Seite nicht gefunden',

  'common.loading': 'Wird geladen …',
  'common.error': 'Fehler',
  'common.retry': 'Erneut versuchen',
  'common.back': 'Zurück',
  'common.none': '—',
  'common.of': 'von',
  'common.yes': 'ja',
  'common.no': 'nein',
  'common.cancel': 'Abbrechen',

  'start.title': 'Demo-Dossier prüfen',
  'start.lead':
    'Der Baueingabe-Check markiert Befunde mit Quelle und § — er entscheidet nicht und bestätigt keine Vollständigkeit. Die Verantwortung bleibt beim Projektverfasser.',
  'start.dossiersTitle': 'Eingebaute Demo-Dossiers',
  'start.dossiersLead': 'Alle eingebauten Dossiers sind synthetisch — keine echten Personendaten.',
  'start.dossiersEmpty': 'Keine eingebauten Dossiers gefunden.',
  'start.synthetic': 'synthetisch',
  'start.startRun': 'Prüflauf starten',
  'start.starting': 'Startet …',
  'start.gemeindenTitle': 'Regelwerk-Abdeckung',
  'start.gemeindenLead':
    'Welche Packs es gibt und wie viele Regeln darin gegen eine Primärquelle verifiziert sind.',
  'start.gemeindenEmpty': 'Kein Regelwerk-Pack gefunden.',

  'coverage.zones': 'Zonen',
  'coverage.rules': 'Regeln wirksam',
  'coverage.verified': 'verifiziert',
  'coverage.unverified': 'nicht verifiziert',
  'coverage.placeholder': 'Platzhalter',
  'coverage.regime': 'Regime',
  'coverage.sources': 'Quellen',
  'coverage.mode.verified': 'verifiziert',
  'coverage.mode.indicative': 'indikativ',

  'run.title': 'Prüflauf',
  'run.lead': 'Die Stufen unten sind die Stufen, die das Backend tatsächlich meldet.',
  'run.id': 'Lauf-ID',
  'run.dossier': 'Dossier',
  'run.stagesTitle': 'Stufen',
  'run.openReport': 'Bericht öffnen',
  'run.failedTitle': 'Der Lauf ist fehlgeschlagen',
  'run.pollError': 'Der Status konnte nicht abgerufen werden.',
  'run.statusLabel': 'Status',
  'run.status.queued': 'in der Warteschlange',
  'run.status.running': 'läuft',
  'run.status.succeeded': 'abgeschlossen',
  'run.status.failed': 'fehlgeschlagen',
  'run.elapsed': 'Läuft seit {minutes} Min.',
  'run.longRunning': 'Dieser Lauf dauert ungewöhnlich lange. Wurde der Server unterdessen neu gestartet, markiert er den Lauf beim Start als fehlgeschlagen; danach lässt sich das Dossier erneut prüfen.',
  'stage.pending': 'ausstehend',
  'stage.running': 'läuft',
  'stage.done': 'erledigt',
  'stage.skipped': 'übersprungen',
  'stage.failed': 'fehlgeschlagen',

  'report.title': 'Bericht',
  'report.gemeinde': 'Gemeinde',
  'report.zone': 'Zone',
  'report.regime': 'Regime',
  'report.case': 'Fall',
  'report.mode': 'Modus',
  'report.ampelTitle': 'Ampel',
  'report.ampelLead': 'Zählt ausschliesslich die harten Befunde.',
  'report.findingsTitle': 'Befunde',
  'report.findingsLead':
    'Nur aus verifizierten Regeln auf einer verifizierten Zone. Jeder Befund nennt Regel, § und die gerechneten Werte.',
  'report.findingsEmpty': 'Keine harten Befunde.',
  'report.indicativeTitle': 'Indikativ — nicht verifiziert',
  'report.indicativeLead':
    'Diese Ausgaben stammen aus Regeln oder Zonen, die noch nicht gegen die BZO verifiziert sind. Sie sind keine Ampel-Befunde und tragen keine Ampelfarbe.',
  'report.indicativeBadge': 'Indikativ · nicht verifiziert',
  'report.indicativeEmpty': 'Keine indikativen Ausgaben.',
  'report.indicativeReason': 'Grund',
  'report.modeIndicativeBanner':
    'Entwurf — noch nicht gegen die BZO verifiziert. In diesem Modus gibt es keine harten Befunde, nur indikative Ausgaben.',
  'report.hinweiseTitle': 'Hinweise',
  'report.hinweiseLead': 'Wertende Aspekte und Indikatoren — keine Befunde.',
  'report.hinweisBadge': 'Hinweis',
  'report.confidence': 'Konfidenz',
  'report.nachweiseTitle': 'Vorbehaltene Nachweise',
  'report.nachweiseLead': 'Später zu erbringen — kein Grund, die Eingabe zurückzuhalten.',
  'report.nachweisDue': 'fällig',
  'report.nachweisOpen': 'offen',
  'report.nachweisDone': 'erbracht',
  'report.skippedTitle': 'Manuell · zu prüfen',
  'report.skippedLead':
    'Diese Regeln liefen nicht. Sie sind weder erfüllt noch verletzt — der Grund steht bei jeder Zeile.',
  'report.skippedReason': 'Grund',
  'report.skippedGroup.missingInput': 'Eingabewert fehlt',
  'report.skippedGroup.missingInputLead':
    'Die Regel würde rechnen — der Wert steht nicht im Dossier. Nachtragen in der Faktenprüfung, dann rechnet der Check.',
  'report.skippedGroup.missingGis': 'GIS-Wert fehlt',
  'report.skippedGroup.missingGisLead':
    'Ohne GIS-Anbindung für die Parzelle nicht prüfbar. Diese Regeln sind manuell gegen die amtlichen Geodaten zu prüfen.',
  'report.skippedGroup.notComputable': 'Regelwerk deckt den Fall nicht',
  'report.skippedGroup.notComputableLead':
    'Das Pack beschreibt die Regel, die Prüfung liess sich daraus aber nicht rechnen. Diese Regeln sind vollständig manuell zu prüfen.',
  'report.skippedGroup.unverified': 'Regel noch nicht verifiziert',
  'report.skippedGroup.unverifiedLead':
    'Die Regel ist im Pack nicht gegen die Primärquelle bestätigt und darf deshalb keinen harten Befund erzeugen.',
  'report.skippedGroup.other': 'Weitere Gründe',
  'report.skippedGroup.otherLead':
    'Gründe, die sich keiner der Gruppen zuordnen liessen — bitte einzeln lesen.',
  'report.skippedGroup.notApplicable': 'Nicht anwendbar in dieser Zone',
  'report.skippedGroup.notApplicableLead':
    'Kein Mangel am Projekt: diese Regeln gelten für ein anderes Regime oder für einen Zonen-Parameter, den diese Zone nicht führt.',
  'report.grenzenTitle': 'Grenzen',
  'report.grenzenLead': 'Was dieser Bericht nicht prüft.',
  'report.rule': 'Regel',
  'report.source': 'Quelle',
  'report.pack': 'Pack',
  'report.computed': 'Gerechnet',
  'report.missing': 'Fehlende Eingabewerte',
  'report.newRun': 'Weiteres Dossier prüfen',
  'report.loadError': 'Der Bericht konnte nicht geladen werden.',

  // --- M3: Befundkarte, ehrliche Abschnitte, Quellenregister ---------------
  'report.asOf': 'Stand',
  'report.actionTitle': 'Was zu tun ist',
  'report.skippedEmpty':
    'Keine Regel wurde übersprungen. Geprüft wurde trotzdem nur, was das Regelwerk-Pack als verifizierte Regel führt — diese Zeile sagt nichts über Themen, die gar keine Regel haben.',
  'report.nachweiseEmpty':
    'Für diese Zone führt das Regelwerk-Pack keine vorbehaltenen Nachweise. Das heisst nicht, dass die Baubehörde keine verlangt — es heisst, dass hier keine hinterlegt sind.',
  'report.hinweiseEmpty': 'Zu diesem Lauf liegen keine Indikatoren vor.',
  'report.grenzenEmpty':
    'Zu diesem Lauf wurden keine Grenzen übermittelt. Der Bericht bleibt eine Markierung von Befunden und keine Aussage über Vollständigkeit.',
  'report.sourcesTitle': 'Quellen und Stand',
  'report.sourcesLead':
    'Die Dokumente, auf die sich das Regelwerk-Pack dieser Gemeinde stützt, mit dem Stand, den das Pack führt. Eine einzelne Regel nennt ihren Stand nur, wenn er in der Regel selbst hinterlegt ist — geraten wird er nie.',
  'report.sourcesEmpty': 'Für dieses Pack sind keine Quelldokumente registriert.',
  'report.sourceNoVersion': 'ohne Versionsangabe im Pack',
  'report.printView': 'Druckfassung',
  'report.pdfDownload': 'PDF herunterladen',

  'confidence.deterministic': 'deterministisch',
  'confidence.retrieval': 'retrieval',
  'confidence.indicator': 'Indikator',
  'confidence.level.high': 'hoch',
  'confidence.level.medium': 'mittel',
  'confidence.level.low': 'tief',
  'confidence.deterministic.meaning':
    'Gerechneter Code hat den Eingabewert gegen den Grenzwert der zitierten Bestimmung verglichen. Kein Modell war an der Rechnung beteiligt.',
  'confidence.retrieval.meaning':
    'Aus einem Regelwerk-Pack, das an dieser Stelle noch nicht gegen die Primärquelle verifiziert ist.',
  'confidence.indicator.meaning':
    'Erfahrungswert ohne Rechtsfolge — kein Befund und keine Ampel.',

  'action.green':
    'Nichts zu tun. Die Regel ist mit den eingegebenen Werten erfüllt; ändert sich ein Wert, ändert sich der Befund.',
  'action.missingArtifacts':
    'Fehlende Unterlage nachreichen: {items}. Danach den Prüflauf wiederholen.',
  'action.missingFormFields':
    'Diese Formularfelder als Zahl eintragen: {items}. Ein Text an Stelle einer Zahl zählt als nicht befüllt.',
  'action.inconsistent':
    'Formular und GIS-Abfrage widersprechen sich. Klären, welcher Wert stimmt, und das Baugesuchsformular entsprechend korrigieren.',
  'action.gisFlag':
    'Die GIS-Abfrage meldet ein Merkmal, das für dieses Grundstück eine eigene Abklärung verlangt. Die zuständige Fachstelle beiziehen.',
  'action.limitNotMet':
    'Der Eingabewert hält den Grenzwert der zitierten Bestimmung nicht ein. Projekt anpassen oder die Abweichung im Baugesuch begründen und belegen.',
  'action.generic':
    'Den Befund gegen die zitierte Bestimmung prüfen und im Baugesuch beantworten.',
  'action.indicative':
    'Diese Regel ist noch nicht gegen die Primärquelle verifiziert. Vor jeder Konsequenz die zitierte Bestimmung selbst lesen — hier steht kein Befund.',

  // --- M3: «Wie lese ich diesen Bericht?» ---------------------------------
  'explainer.title': 'Wie lese ich diesen Bericht?',
  'explainer.lead':
    'Was die Ampel aussagt, woher eine Zahl kommt, warum eine Regel nicht gelaufen ist — und wo die Verantwortung bleibt.',
  'explainer.show': 'Erklärung einblenden',
  'explainer.hide': 'Erklärung ausblenden',
  'explainer.lights.title': 'Die drei Ampelfarben',
  'explainer.lights.body':
    'Rot: eine verifizierte Regel ist mit den vorliegenden Werten nicht erfüllt. Amber: die Regel greift, aber ein Eingabewert fehlt oder ist offen — der Punkt ist ungeklärt, nicht in Ordnung. Grün: die Regel ist mit den vorliegenden Werten erfüllt. Grün ist eine Aussage über genau eine Regel und über genau die eingegebenen Werte — nie über das Dossier und nie über das Verfahren.',
  'explainer.lights.note':
    'Jede Ampel trägt Symbol und Wort, mit drei verschiedenen Symbolen. Wer den Bericht schwarzweiss ausdruckt oder Farben schlecht unterscheidet, verliert nichts.',
  'explainer.confidence.title': 'Der Konfidenz-Tag auf jeder Karte',
  'explainer.confidence.body':
    'Er sagt, wie ein Eintrag zustande gekommen ist — nicht, wie wahrscheinlich er zutrifft. Es gibt genau drei:',
  'explainer.confidence.deterministic':
    'deterministisch · hoch — die Zahlen stammen aus dem Formular oder aus den Plänen, und verglichen hat sie gerechneter Code gegen den Grenzwert der zitierten Bestimmung.',
  'explainer.confidence.retrieval':
    'retrieval · mittel — die Regel stammt aus einem Regelwerk-Pack, das an dieser Stelle noch nicht gegen die Primärquelle verifiziert ist. Solche Einträge stehen im indikativen Block und tragen nie eine Ampel.',
  'explainer.confidence.indicator':
    'Indikator · tief — ein Erfahrungswert ohne Rechtsfolge, etwa ein Hinweis auf ein Einsprache-Risiko. Er steht unter «Hinweise» und ist kein Befund.',
  'explainer.numbers.title': 'Die Zahlen auf den Karten',
  'explainer.numbers.body':
    'Was auf einer Karte in der Monospace-Schrift steht, ist genau das, was die deterministische Prüfung gerechnet hat: Parameter, Eingabewert, Operator, Grenzwert. Nichts davon ist aus dem Meldungstext zurückgelesen, nichts nachträglich gerundet. Die Einheit stammt aus dem Namen des Parameters; trägt der Name keine, steht keine da.',
  'explainer.numbers.example':
    'Beispiel: baumasse_hauptbau_m3 = 1’649.86 m³ ≤ 1’650 m³ heisst — dieser Eingabewert, dieser Grenzwert, dieser Vergleich.',
  'explainer.skipped.title': 'Warum manche Regeln nicht gelaufen sind',
  'explainer.skipped.body':
    'Eine Regel läuft nur, wenn ihre Eingabewerte auflösen. Fehlt ein Wert, gilt in dieser Gemeinde ein anderes Regime (Baumassenziffer statt Ausnützungsziffer), oder ist die Regel noch nicht verifiziert, dann steht sie unter «Manuell · zu prüfen» — mit Grund. Übersprungen heisst weder erfüllt noch verletzt. Diese Liste ist der Teil, den Sie selbst prüfen.',
  'explainer.indicative.title': 'Was indikative Ausgaben sind',
  'explainer.indicative.body':
    'Gestrichelter Rahmen, graues Badge, keine Ampelfarbe: die Regel oder die Zone ist noch nicht gegen die BZO verifiziert. Solche Ausgaben werden nie unter die Befunde gemischt und zählen nicht in der Ampel. Sie sagen, wo nachzuschauen ist — sie sagen kein Ergebnis.',
  'explainer.nachweise.title': 'Vorbehaltene Nachweise',
  'explainer.nachweise.body':
    'Nachweise, die erst später fällig werden — Energie, Brandschutz, Schall. Sie sind kein Grund, die Baueingabe zurückzuhalten, und sie stehen im Bericht, damit sie im richtigen Moment nicht fehlen.',
  'explainer.liability.title': 'Wo die Verantwortung liegt',
  'explainer.liability.body':
    'Der Baueingabe-Check markiert Befunde mit Quelle und § und stellt daneben, was er nicht geprüft hat. Er entscheidet nicht und bestätigt keine Vollständigkeit. Die Verantwortung für die Baueingabe bleibt beim Projektverfasser. Auch «keine roten Befunde» ist keine Aussage über das Dossier, sondern über die Regeln, die dieses Pack führt.',

  // --- M3: Druckfassung ----------------------------------------------------
  'print.date': 'Datum',
  'print.page': 'Seite',
  'print.footnote':
    'Druckfassung des Berichts. Sie enthält nichts, was der Bericht am Bildschirm nicht zeigt, und keine zusammenfassende Beurteilung.',

  'severity.red': 'Rot',
  'severity.amber': 'Amber',
  'severity.green': 'Grün',
  'severity.red.meaning': 'Regel nicht erfüllt',
  'severity.amber.meaning': 'Eingabewert fehlt oder offen',
  'severity.green.meaning': 'Regel erfüllt',

  // --- M2: Anmeldung, Einladung, Workspace, Upload -----------------------
  'auth.title': 'Anmelden',
  'auth.lead': 'Der Baueingabe-Check arbeitet ohne Passwort. Sie fordern einen Anmeldelink an; er ist einmalig gültig und läuft nach kurzer Zeit ab.',
  'auth.redeeming': 'Anmeldelink wird eingelöst …',
  'auth.linkFailedTitle': 'Dieser Anmeldelink funktioniert nicht mehr',
  'auth.linkFailedNext': 'Fordern Sie unten einen neuen Link an. Der neue Link entwertet alle älteren.',
  'auth.requestTitle': 'Anmeldelink anfordern',
  'auth.emailLabel': 'E-Mail-Adresse',
  'auth.emailPlaceholder': 'vorname.name@buero.ch',
  'auth.requestAction': 'Anmeldelink anfordern',
  'auth.requesting': 'Wird angefordert …',
  // Which of these two the sign-in screen shows is decided by the server
  // (`mail_delivery` on /health), never by a build-time flag: the same bundle
  // runs on an installation that sends mail and on one that does not.
  'auth.mailNotice.delivers': 'Der Anmeldelink kommt per E-Mail. Prüfen Sie Ihr Postfach — und den Spam-Ordner, falls nichts ankommt.',
  'auth.mailNotice.logOnly': 'Diese Installation versendet keine E-Mail. Der Anmeldelink wird ins Server-Log geschrieben; Velox-KAi stellt ihn Ihnen persönlich zu.',
  'auth.signOut': 'Abmelden',
  'invite.title': 'Einladung',
  'invite.redeeming': 'Einladung wird eingelöst …',
  'invite.noToken': 'Dieser Einladungslink ist unvollständig — der Token fehlt. Bitte den Link aus der Nachricht vollständig öffnen oder beim Absender eine neue Einladung anfordern.',
  'invite.failedTitle': 'Diese Einladung lässt sich nicht einlösen',
  'invite.failedNext': 'Bitte beim Absender eine neue Einladung anfordern. Wer bereits ein Konto hat, meldet sich stattdessen mit einem Anmeldelink an.',
  'invite.toSignIn': 'Zur Anmeldung',
  'invite.state.open': 'offen',
  'invite.state.accepted': 'eingelöst',
  'invite.state.revoked': 'widerrufen',
  'invite.state.expired': 'abgelaufen',
  'invitePanel.title': 'Einladungen',
  'invitePanel.lead': 'Der Link erscheint genau einmal und ist danach nicht mehr abrufbar — der Server speichert nur seinen Hash.',
  'invitePanel.emailLabel': 'E-Mail-Adresse',
  'invitePanel.roleLabel': 'Rolle',
  'invitePanel.roleMember': 'Mitglied',
  'invitePanel.roleAdmin': 'Workspace-Admin',
  'invitePanel.create': 'Einladung erzeugen',
  'invitePanel.linkOnce': 'Einladungslink — wird nur jetzt angezeigt',
  'invitePanel.copy': 'Kopieren',
  // `mail_sent` on the created invite decides between these two — it is the
  // server's report of what happened, not an assumption about the transport.
  'invitePanel.noMail': 'Es wurde keine E-Mail versendet. Bitte den Link persönlich zustellen.',
  'invitePanel.mailSent': 'Die Einladung wurde per E-Mail versendet. Der Link steht trotzdem hier — einmalig, falls die Nachricht nicht ankommt.',
  'invitePanel.revoke': 'Widerrufen',
  'invitePanel.empty': 'Noch keine Einladung erzeugt.',
  'workspace.switchLabel': 'Workspace',
  'workspace.switchNone': 'Bitte wählen',
  'workspace.indexLink': 'Alle Workspaces',
  'workspace.role': 'Rolle',
  'workspace.role.founder': 'Founder',
  'workspace.role.admin': 'Admin',
  'workspace.role.member': 'Mitglied',
  'workspace.uploadCta': 'Dossier hochladen',
  'workspace.dossiersTitle': 'Dossiers in diesem Workspace',
  'workspace.dossiersLead': 'Hochgeladene und übernommene Dossiers. Sie bleiben in diesem Workspace.',
  'workspace.dossiersEmpty': 'Noch kein Dossier in diesem Workspace.',
  'workspace.kindUpload': 'Upload',
  'workspace.kindDemo': 'Demo',
  'workspace.attested': 'attestiert',
  'workspace.notFound': 'Dieser Workspace ist für dieses Konto nicht sichtbar.',
  'workspace.retention': 'Aufbewahrung: {days} Tage. Danach werden Dateien, Fakten und Prüfläufe automatisch und endgültig gelöscht.',
  'workspace.window.title': 'Aufbewahrungsfrist dieses Workspace',
  'workspace.window.lead': 'Wie lange Dossiers dieses Workspace gespeichert bleiben. Erlaubt sind {min} bis {max} Tage.',
  'workspace.window.label': 'Frist in Tagen',
  'workspace.window.optional': 'Leer lassen übernimmt den Default des Servers.',
  'workspace.window.save': 'Frist setzen',
  'workspace.window.asymmetry': 'Verkürzen wirkt rückwirkend: die bereits gespeicherten Dossiers werden aus ihrem eigenen Hochladedatum neu datiert — ein Dossier jenseits der neuen Frist ist damit sofort fällig. Verlängern wirkt nur auf künftige Uploads; eine einmal gegebene kürzere Zusage wird nie verlängert.',
  'workspace.window.saved': 'Frist auf {days} Tage gesetzt.',
  'workspace.window.backups': 'Sicherungskopien laufen unabhängig davon aus: die tatsächliche Löschung liegt später als die hier gesetzte Frist.',
  'workspace.window.confirm.title': 'Frist wirklich verkürzen?',
  'workspace.window.confirm.change': 'Von {from} auf {to} Tage.',
  'workspace.window.confirm.due': '{count} von {total} gespeicherten Dossiers sind damit sofort fällig.',
  'workspace.window.confirm.dueNone': 'Kein gespeichertes Dossier wird dadurch sofort fällig — es verschwindet jetzt nichts.',
  'workspace.window.confirm.redated': 'Neu datiert werden {count} von {total} Dossiers, gerechnet aus ihrem eigenen Hochladedatum.',
  'workspace.window.confirm.what': 'Ein sofort fälliges Dossier entfernt der nächste Löschlauf — er läuft im Hintergrund und wartet auf keinen weiteren Klick. Weg sind dann die hochgeladenen PDFs, die extrahierten Fakten und alle Prüfläufe samt Berichten.',
  'workspace.window.confirm.unknown': 'Die Dossierliste dieses Workspace ist gerade nicht geladen — wie viele Dossiers das trifft, lässt sich hier nicht sagen.',
  'workspace.window.confirm.cta': 'Frist verkürzen',
  'workspace.window.confirm.busy': 'Wird gesetzt …',
  'workspace.mode.label': 'Datenmodus',
  'workspace.mode.title': 'Datenmodus dieses Workspace',
  'workspace.mode.lead': 'Der Datenmodus bestimmt, welche Daten hier hineindürfen und ob der Extraktionsschritt das Sprachmodell überhaupt verwenden darf.',
  'workspace.mode.current': 'aktiv',
  'workspace.mode.locked': 'gesperrt',
  'workspace.mode.lockedReason': 'Dieser Modus ist auf diesem Server nicht freigeschaltet und lässt sich in der Anwendung nicht auswählen.',
  'workspace.mode.liveLockedReason': 'Live bedeutet: echte Personendaten aus einem laufenden Verfahren. Der Modus lässt sich nicht in der Anwendung wählen — er wird auf dem Server freigeschaltet, und zwar erst, wenn ein unterzeichneter DPA vorliegt und entweder ein verifizierter CH/EU-Host konfiguriert ist oder die Übermittlung des Extraktions-Schritts an den US-Endpoint ausdrücklich akzeptiert wurde. Auf diesem Server ist keines von beidem gesetzt.',
  'workspace.mode.unknown': 'unbekannt',
  'workspace.mode.demo': 'Demo',
  'workspace.mode.anonymised': 'Anonymisiert',
  'workspace.mode.live': 'Live',
  'workspace.mode.demoHint': 'Nur die eingebauten synthetischen Dossiers.',
  'workspace.mode.anonymisedHint': 'Test-Dossiers ohne echte Personendaten — der Standard für ein Prospect-Büro.',
  'workspace.mode.liveHint': 'Echte Personendaten aus einem laufenden Verfahren. Dateien und Fakten liegen auf diesem Server; der Modell-Schritt ist hier nicht freigeschaltet, der Lauf bleibt deterministisch.',
  'workspace.mode.liveHintUs': 'Echte Personendaten aus einem laufenden Verfahren. Dateien und Fakten liegen auf diesem Server; für die Extraktion werden Formulartext und gerenderte Planseiten an Anthropic (Vereinigte Staaten) übermittelt — Founder-Entscheid vom 20.08.2026, kein CH/EU-Host im Einsatz.',
  'workspaceIndex.title': 'Workspaces',
  'workspaceIndex.leadFounder': 'Jedes Büro arbeitet in einem eigenen Workspace: eigene Dossiers, eigene Läufe, eigener Audit-Trail.',
  'workspaceIndex.leadMember': 'Sie sehen ausschliesslich den Workspace Ihres Büros.',
  'workspaceIndex.emptyFounder': 'Es gibt noch keinen Workspace. Legen Sie unten den ersten an.',
  'workspaceIndex.emptyMember': 'Dieses Konto gehört zu keinem Workspace. Bitte den Absender der Einladung kontaktieren.',
  'workspaceIndex.createTitle': 'Workspace anlegen',
  'workspaceIndex.createLead': 'Ein neuer Workspace startet im Datenmodus «Anonymisiert».',
  'workspaceIndex.nameLabel': 'Name des Büros',
  'workspaceIndex.create': 'Anlegen',
  'upload.title': 'Dossier hochladen',
  'upload.lead': 'Pläne, Formulare und Berechnungen als PDF. Danach läuft der Pre-Check über die Stufen, die das Backend meldet.',
  'upload.filesTitle': 'Dateien',
  'upload.caps': 'Bis {files} PDF, je höchstens {perFile}, zusammen höchstens {total}. Die Seitenzahl ist auf {pages} Seiten begrenzt und wird auf dem Server geprüft.',
  'upload.dropHere': 'PDF hierher ziehen',
  'upload.orChoose': 'Oder Dateien auswählen:',
  'upload.accepted': 'übernommen',
  'upload.noFiles': 'Noch keine Datei ausgewählt.',
  'upload.summary': '{accepted} übernommen, {rejected} abgewiesen, zusammen {size}.',
  'upload.removeFile': '{name} entfernen',
  'upload.contextTitle': 'Einordnung',
  'upload.contextLead': 'Gemeinde, Bautyp und Verfahren bestimmen, welches Regelwerk-Pack greift.',
  'upload.gemeinde': 'Gemeinde',
  'upload.bautyp': 'Bautyp',
  'upload.verfahren': 'Verfahren',
  'upload.pleaseChoose': 'Bitte wählen',
  'upload.zone': 'Zone (optional)',
  'upload.zonePlaceholder': 'z. B. W2.2 — sonst aus dem Baugesuch gelesen',
  'upload.dossierTitle': 'Bezeichnung (optional)',
  'upload.dossierTitlePlaceholder': 'z. B. Neubau EFH Musterweg',
  'upload.missingGemeinde': 'Ihre Gemeinde fehlt in der Liste? Dann gibt es dafür noch kein Regelwerk-Pack. Laden Sie die BZO der Gemeinde hoch: daraus entsteht ein Entwurfs-Pack, das hier anschliessend wählbar ist.',
  'upload.attestTitle': 'Attestierung',
  'upload.attestLead': 'Ohne diese Bestätigung ist kein Upload möglich. Sie wird im Audit-Trail festgehalten.',
  'upload.attestCheckbox': 'Dieses Test-Dossier enthält keine echten Personendaten / ist anonymisiert',
  'upload.attestHelp': 'Dieser Workspace läuft im Datenmodus «{mode}». In den Modi Demo und Anonymisiert gehören keine echten Personendaten in ein Dossier — die Bestätigung ist Pflicht und wird im Audit-Trail festgehalten.',
  'upload.attestCheckboxLiveUs': 'Dieses Dossier enthält echte Personendaten. Ich bin berechtigt, sie hier zu bearbeiten. Die Dateien werden auf diesem Server gespeichert und verarbeitet; der Extraktions-Schritt übermittelt Formulartext und gerenderte Planseiten an Anthropic (Anthropic PBC, Vereinigte Staaten). Alle Berechnungen und Regelprüfungen bleiben auf diesem Server.',
  'upload.attestCheckboxLiveLocal': 'Dieses Dossier enthält echte Personendaten. Ich bin berechtigt, sie hier zu bearbeiten. Die Dateien werden ausschliesslich auf diesem Server gespeichert und verarbeitet; der Modell-Schritt ist für diesen Workspace nicht freigeschaltet, es werden keine Inhalte an Dritte übermittelt.',
  'upload.attestHelpLive': 'Dieser Workspace läuft im Datenmodus «{mode}» — echte Personendaten. Die Bestätigung wird mit Person und Zeitpunkt im Audit-Trail festgehalten. Was mit den Dateien geschieht, steht im Satz darüber; der Baueingabe-Check prüft weder Ihre Berechtigung noch den Inhalt.',
  'upload.submit': 'Hochladen und prüfen',
  'upload.submitting': 'Wird hochgeladen …',
  'upload.blockedAttest': 'Bitte zuerst die Attestierung bestätigen.',
  'upload.blockedFiles': 'Bitte mindestens ein PDF auswählen.',
  'upload.blockedGemeinde': 'Bitte die Gemeinde wählen.',
  'upload.reject.notPdf': 'Kein PDF — geprüft wurde der Inhalt, nicht die Endung.',
  'upload.reject.tooLarge': 'Zu gross — höchstens {max} pro Datei.',
  'upload.reject.totalTooLarge': 'Gesamtgrösse überschritten — höchstens {total}.',
  'upload.reject.tooMany': 'Zu viele Dateien — höchstens {files}.',
  'upload.reject.duplicate': 'Bereits ausgewählt (gleicher Name, gleiche Grösse).',
  'upload.reject.empty': 'Die Datei ist leer.',
  'upload.bautyp.neubau': 'Neubau',
  'upload.bautyp.umbau': 'Umbau',
  'upload.bautyp.anbau': 'Anbau',
  'upload.bautyp.aufstockung': 'Aufstockung',
  'upload.bautyp.abbruch-wiederaufbau': 'Abbruch und Wiederaufbau',
  'upload.bautyp.ersatzneubau': 'Ersatzneubau',
  'upload.verfahren.ordentlich': 'Ordentliches Verfahren',
  'upload.verfahren.vereinfacht': 'Vereinfachtes Verfahren',
  'upload.verfahren.anzeigeverfahren': 'Anzeigeverfahren',
  'run.llmBlocked': 'Der Extraktionsschritt mit Sprachmodell ist für diesen Workspace blockiert. Der Lauf ist rein deterministisch; Felder, die nur die Bild- und Textanalyse liefern kann, bleiben leer.',
  'run.isRecheck': 'Dieser Lauf ist eine deterministische Neuprüfung nach einer Faktenkorrektur — ohne Modell-Aufruf.',
  'run.openParent': 'Vorlauf öffnen',
  'report.isRecheck': 'Dieser Bericht stammt aus einer deterministischen Neuprüfung nach einer Faktenkorrektur.',
  'report.openParentReport': 'Bericht des Vorlaufs',

  // --- M2: Faktenprüfung (Vertrauensoberfläche) ---------------------------
  'facts.cta': 'Fakten prüfen',
  'facts.title': 'Faktenprüfung',
  'facts.lead':
    'Jeder Wert, mit dem gerechnet wird — mit seiner Herkunft und der Konfidenz der Quelle. Korrigieren Sie, was falsch ist: die deterministischen Checks laufen sofort erneut, ohne Modell-Aufruf, und der Vergleich zeigt, welche Befunde sich dadurch bewegt haben.',
  'facts.openReport': 'Bericht öffnen',
  'facts.recheckNow': 'Jetzt neu prüfen',
  'facts.llmStatus': 'Extraktion',
  'facts.noFactsYet':
    'Für dieses Dossier liegen noch keine Fakten vor. Der Prüflauf extrahiert sie; danach lassen sie sich hier prüfen und korrigieren.',
  'facts.empty': 'leer',
  'facts.valueMissing': 'Eingabewert fehlt',
  'facts.edit': 'Ändern',
  'facts.enter': 'Wert eintragen',
  'facts.save': 'Speichern und neu prüfen',
  'facts.clear': 'leeren',
  'facts.editHelp': 'Leer lassen heisst: kein Wert. Geprüft wird der Typ; ob der Wert eine Regel erfüllt, entscheidet die Engine.',
  'facts.listHelp': 'Kurznamen, mit Komma getrennt.',
  'facts.listPlaceholder': 'z. B. baugesuch, situationsplan',
  'facts.notEditable': 'Nicht über die Faktenprüfung änderbar — dieses Feld identifiziert Grundstück oder Person und bleibt aus dem Audit-Trail heraus.',
  'facts.notEditableGis': 'Nicht über die Faktenprüfung änderbar — dieser Wert stammt aus der GIS-Abfrage zur Parzelle, nicht aus dem Dossier.',
  'facts.saving': 'Korrektur wird gespeichert …',
  'facts.rechecking': 'Wird deterministisch neu geprüft …',
  'facts.recheckTitle': 'Neuprüfung',
  'facts.recheckMeta': 'Deterministisch neu geprüft in {ms} ms — ohne Modell-Aufruf.',
  'facts.confidence': 'Konfidenz',
  'facts.prov.deterministisch': 'deterministisch',
  'facts.prov.deterministisch.meaning': 'Aus dem Formular oder dem Plantext gelesen bzw. daraus gerechnet.',
  'facts.prov.llm': 'LLM',
  'facts.prov.llm.meaning': 'Vom Sprach-/Bildmodell aus den Plänen gelesen — vom Menschen zu prüfen.',
  'facts.prov.gis': 'GIS',
  'facts.prov.gis.meaning': 'Aus einer GIS-Abfrage zur Parzelle.',
  'facts.prov.manuell': 'manuell',
  'facts.prov.manuell.meaning': 'Von einer Person in der Faktenprüfung eingetragen oder korrigiert.',
  'facts.prov.unbekannt': 'unbekannt',
  'facts.prov.unbekannt.meaning': 'Zu diesem Wert kam keine Herkunft mit. Sie wird nicht geraten.',
  'facts.missingTitle': 'Fehlende Eingaben',
  'facts.missingLead':
    'Werte, die nicht zuverlässig gelesen werden konnten. Sie sind bewusst leer und nicht geraten. Zu jedem Eintrag steht, welche Regeln deswegen nicht gerechnet wurden.',
  'facts.missingEmpty': 'Keine fehlenden Eingaben — alle benötigten Werte liegen vor.',
  'facts.missingNoneBlocking':
    'Kein fehlender Wert blockiert derzeit eine Regel.',
  'facts.missingRest': '{count} weitere fehlende Werte, die keine Regel blockieren',
  'facts.missingRestOne': '1 weiterer fehlender Wert, der keine Regel blockiert',
  'facts.missingRestLead':
    'Auch diese Werte fehlen — sie halten aber keinen Check auf. Nachtragen lohnt sich, sobald eine Regel sie braucht.',
  'facts.blocks': 'Blockiert die Regeln',
  'facts.blocksNone': 'Blockiert derzeit keine Regel dieses Regelwerk-Packs.',
  'facts.jumpToField': 'Zum Feld',
  'facts.group.stammdaten': 'Stammdaten',
  'facts.group.values': 'Werte und Kennzahlen',
  'facts.group.form': 'Formularfelder (eBaugesuch)',
  'facts.group.flags': 'Merkmale',
  'facts.group.gis': 'GIS-Abfrage',
  'facts.group.dokumente': 'Erkannte Unterlagen',
  'facts.group.nachweise': 'Nachweise',
  'facts.filesTitle': 'Grundlage: hochgeladene Dateien',
  'facts.pages': 'Seiten',
  'facts.kind.vector': 'Vektor (CAD-Export, Text lesbar)',
  'facts.kind.raster': 'Scan (nur Bild)',
  'facts.kind.mixed': 'gemischt',
  'facts.kind.unknown': 'Art unbekannt',

  // --- M2: Lauf-Hinweise, Diff, interne Kosten ----------------------------
  'notices.title': 'Hinweise zum Lauf',
  'notices.runTitle': 'Hinweise zum Lauf',
  'notices.reportTitle': 'Wie dieser Lauf zustande kam',
  'notices.recheckTitle': 'Hinweise zur Neuprüfung',
  'notices.stage': 'Stufe',
  'diff.title': 'Vergleich',
  'diff.sectionTitle': 'Was sich gegenüber dem Vorlauf geändert hat',
  'diff.sectionLead': 'Verglichen wird über die Regel-ID. Die Faktenzeilen stammen aus dem Audit-Trail.',
  'diff.factsTitle': 'Korrigierte Fakten',
  'diff.findingsTitle': 'Bewegte Befunde',
  'diff.change.neu': 'neu',
  'diff.change.entfallen': 'entfallen',
  'diff.change.schwere': 'Schweregrad geändert',
  'diff.change.wert': 'Wert geändert',
  'diff.wasAbsent': 'vorher nicht vorhanden',
  'diff.isGone': 'jetzt nicht mehr vorhanden',
  'diff.moved': 'geändert',
  'diff.unchanged': '{count} Befund(e) unverändert.',
  'cost.title': 'Token-Kosten (intern)',
  'cost.input': 'Eingabe',
  'cost.output': 'Ausgabe',
  'cost.calls': 'Modell-Aufrufe',
  'cost.budget': 'Obergrenze',
  'cost.exceeded': 'Obergrenze gerissen — der Lauf ist deswegen fehlgeschlagen.',

  // --- M4: Regelwerk-Manager, BZO-Ingestion, Verifikation -------------------
  'nav.rulebase': 'Regelwerk',

  'rulebase.title': 'Gemeinden & Regelwerk',
  'rulebase.lead':
    'Jedes Pack mit seinem Verifikationsstand. Verifiziert heisst: ein Mensch hat den Wert gegen die zitierte BZO-Stelle geprüft. Alles andere ist ein Entwurf und läuft indikativ.',
  'rulebase.summary':
    '{total} Gemeinden · {verified} verifiziert · {drafts} Entwürfe.',
  'rulebase.summaryOne': '{total} Gemeinden · {verified} verifiziert · 1 Entwurf.',
  'rulebase.empty': 'Kein Regelwerk-Pack gefunden.',
  'rulebase.draftBadge': 'Entwurf · nicht verifiziert',
  'rulebase.draftNote': 'Entwurf — noch nicht gegen die BZO verifiziert.',
  'rulebase.verifiedBadge': 'verifiziert',
  'rulebase.verifiedBadgeCount': 'verifiziert · {verified}/{total} Regeln',
  'rulebase.openCountBadge': '{count} offen',
  'packDetail.lead':
    'Dieses Pack ist committet und liegt im Regelwerk-Repository. Die Seite ist nur lesbar: sie zeigt jede Zonenzeile mit ihren Parametern und jede Regel, die ein Lauf für diese Gemeinde lädt — mit Status, Quelle und dem, was sie prüft.',
  'packDetail.coverageTitle': 'Verifikationsstand',
  'packDetail.hoehenregime': 'Höhenrecht',
  'packDetail.bzoVersion': 'Fassung',
  'packDetail.loadError': 'Das Regel-Detail dieses Packs liess sich nicht laden',
  'packDetail.zonesTitle': 'Zonen',
  'packDetail.zonesLead':
    'Jede Zeile der Zonenmatrix, wie die Engine sie liest. Nur eine verifizierte Zeile kann einen harten Befund tragen; eine offene Zeile rechnet indikativ.',
  'packDetail.zonesEmpty': 'Für dieses Pack meldet der Server keine Zonenzeilen.',
  'packDetail.zonesCaption': 'Zonenmatrix {gemeinde}: {count} Zeilen',
  'packDetail.zoneCode': 'Zone',
  'packDetail.zoneStatus': 'Status',
  'packDetail.zoneParameters': 'Parameter',
  'packDetail.zoneAliases': 'auch: {aliases}',
  'packDetail.zoneRegime': 'Regime {regime}',
  'packDetail.zoneNoParameters': 'keine zonenweiten Parameter',
  'packDetail.zoneVerifiedFrom': 'Beleg: {source}',
  'packDetail.zoneVerifiedOn': 'verifiziert am {date}',
  'packDetail.zoneHeldBack': 'Zurückgehalten, nicht für die Engine: {values}',
  'packDetail.zoneNull': 'kein Wert',
  'packDetail.layersTitle': 'Regeln nach Ebene',
  'packDetail.layersLead':
    'Ein Lauf für diese Gemeinde lädt Bund, Kanton und Gemeinde übereinander. Pro Ebene stehen unten ihre Regeln, in der Reihenfolge, in der die Engine sie lädt.',
  'packDetail.layersEmpty': 'Für dieses Pack meldet der Server keine Ebenen.',
  'packDetail.layerRules': '{count} Regeln',
  'packDetail.layerRulesOne': '1 Regel',
  'packDetail.ruleType': 'Typ',
  'packDetail.ruleType.computable': 'rechenbar',
  'packDetail.ruleType.required': 'Pflichtunterlage',
  'packDetail.ruleType.nachweis': 'Nachweis',
  'packDetail.ruleType.narrative': 'wertend',
  'packDetail.ruleChecks': 'Prüft',
  'packDetail.ruleNoCheck': 'Wertende Regel — kein Vergleich; der Bericht ordnet ein, mit tieferer Confidence.',
  'packDetail.ruleSeverity': 'Bei Verstoss',
  'packDetail.ruleInputs': 'Eingaben',
  'packDetail.ruleAppliesTo': 'Gilt für',
  'packDetail.ruleVerifyNote': 'Noch zu prüfen',
  'packDetail.ruleVorbehalt': 'Vorbehalt',
  'packDetail.ruleUnverified':
    'Nicht verifiziert — diese Regel erzeugt keinen harten Befund, nur einen Hinweis «Regel noch nicht verifiziert».',
  'packDetail.ruleIdsNote':
    'Die Regeln oben sind genau die, die ein Lauf für diese Gemeinde lädt. Ändern lässt sich hier nichts: ein committetes Pack ändert nur ein geprüfter Diff an rulebase/{pack}/rules.yaml und zones.yaml im Repository.',
  'rulebase.draftSectionTitle': 'Entwürfe — warten auf Verifikation',
  'rulebase.verifiedSectionTitle': 'Verifizierte Packs',
  'rulebase.zones': 'Zonenzeilen',
  'rulebase.zoneParameters': 'Zonen-Parameter',
  'rulebase.rules': 'Regeln wirksam',
  'rulebase.verified': 'verifiziert',
  'rulebase.open': 'offen',
  'rulebase.sources': 'Quelldokumente',
  'rulebase.noSources': 'Kein Quelldokument hinterlegt.',
  'rulebase.pages': '{count} Seiten',
  'rulebase.ingestedAt': 'ingestiert am {date}',
  'rulebase.openPack': 'Öffnen und verifizieren',
  'rulebase.openPackVerified': 'Pack ansehen',
  'rulebase.noPackFor': 'Fehlt eine Gemeinde? Die BZO lässt sich hier ingestieren.',

  'rulebase.ingestCta': 'BZO ingestieren',
  'rulebase.ingestTitle': 'BZO ingestieren',
  'rulebase.ingestLead':
    'Für eine Gemeinde ohne Pack: die öffentliche BZO wird gelesen und daraus ein Entwurfs-Pack erstellt. Der Entwurf enthält nur Daten mit Quellenauszug und Seitenzahl — keine Formel und nichts Verifiziertes.',
  'rulebase.ingestGemeinde': 'Gemeinde',
  'rulebase.ingestGemeindePlaceholder': 'z. B. Zollikon',
  'rulebase.ingestKanton': 'Kanton',
  'rulebase.ingestFileTitle': 'BZO-Dokument',
  'rulebase.ingestDropHere': 'BZO-PDF hierher ziehen',
  'rulebase.ingestOrChoose': 'oder Datei wählen',
  'rulebase.ingestNoFile': 'Noch keine Datei gewählt.',
  'rulebase.ingestNotPdf': 'Kein PDF — geprüft wurde der Inhalt, nicht die Endung.',
  'rulebase.ingestFileDescription': 'Ein PDF mit Textebene, höchstens {max}.',
  'rulebase.ingestTooLarge': 'Grösser als {max}.',
  'rulebase.ingestSubmit': 'Ingestion starten',
  'rulebase.ingestSubmitting': 'Startet …',
  'rulebase.ingestBlockedFile': 'Bitte zuerst ein BZO-PDF wählen.',
  'rulebase.ingestBlockedGemeinde': 'Bitte zuerst die Gemeinde eintragen.',
  'rulebase.ingestReplaceTitle': 'Für diese Gemeinde gibt es bereits einen Entwurf.',
  'rulebase.ingestReplace': 'Bestehenden Entwurf ersetzen',
  'rulebase.ingestScopeNote':
    'Der Entwurf gehört diesem Workspace. Ein anderes Büro sieht ihn nicht.',

  'rulebase.ingestAttestTitle': 'Bestätigung',
  'rulebase.ingestAttestCheckbox': 'Dies ist ein öffentlich zugängliches Dokument der Gemeinde (Bau- und Zonenordnung) und enthält keine Personendaten.',
  'rulebase.ingestBlockedAttest': 'Bitte zuerst die Bestätigung ankreuzen.',
  'rulebase.ingestionTitle': 'BZO-Ingestion',
  'rulebase.ingestionLead': 'Die Stufen unten sind die Stufen, die das Backend tatsächlich meldet.',
  'rulebase.ingestionStagesTitle': 'Stufen',
  'rulebase.ingestionFailed': 'Die Ingestion ist fehlgeschlagen',
  'rulebase.ingestionPollError': 'Der Status konnte nicht abgerufen werden.',
  'rulebase.ingestionOpenPack': 'Entwurfs-Pack öffnen',
  'rulebase.ingestionGemeinde': 'Gemeinde',
  'rulebase.shadowedSectionTitle': 'Entwürfe, die nicht verwendet werden',
  'rulebase.shadowedTitle': 'Dieser Entwurf wird nicht verwendet.',
  'rulebase.shadowedLead':
    'Für diese Gemeinde gibt es bereits ein verifiziertes Pack im Regelwerk. Der Entwurf bleibt lesbar, aber kein Prüflauf greift darauf zu.',
  'rulebase.conflicts': 'Betroffen',

  'verify.title': 'Verifikation gegen die BZO',
  'verify.lead':
    'Links steht, was aus dem Dokument gelesen wurde. Rechts steht die Stelle, aus der es stammt. Bestätigen Sie nur, was Sie dort tatsächlich sehen — und korrigieren Sie den Rest; beides kostet gleich viel.',
  'verify.packNotFound': 'Für diese Adresse gibt es kein Pack.',
  'verify.exportCta': 'Als YAML exportieren',
  'verify.exportHint':
    'Der Export ist der Weg ins Repository: Die Dateien werden über den normalen Git-Fluss committet, der Diff bleibt lesbar.',
  'verify.zonesTitle': 'Zonen-Parameter',
  'verify.zonesLead':
    'Eine Zeile ist eine Zeile der BZO-Zonentabelle. Sammelbestätigung gibt es genau auf dieser Ebene — für ein ganzes Pack nicht.',
  'verify.rulesTitle': 'Regel-Kandidaten',
  'verify.rulesLead':
    'Der Entwurf enthält bewusst keine Formel. Ein rechenbarer Vergleich entsteht erst, wenn ein Mensch Operator und Wert aus der zitierten Stelle einträgt.',
  'verify.zonesEmpty': 'Der Entwurf enthält keine Zonenzeilen.',
  'verify.rulesEmpty': 'Der Entwurf enthält keine Regel-Kandidaten.',
  'verify.noWholePack':
    'Es gibt keine Schaltfläche, die ein ganzes Pack bestätigt — weder hier noch im Backend. Verifiziert wird pro Zonenzeile und pro Regel, jeweils mit der Quellenangabe.',
  // Founder-Entscheid 2026-09-16: eine bestätigte Zelle wird im Workspace zum
  // harten Befund — verifiziert darum nur Velox-KAi. Mitglieder lesen alles.
  'verify.founderOnlyNote':
    'Die Verifikation gegen die Primärquelle erfolgt durch Velox-KAi. Ihr Entwurf bleibt erhalten und wird als «indikativ» geprüft. Sie können hier jede Zeile mit ihrer Quelle lesen.',
  'verify.founderOnlyRow': 'Bestätigen und Korrigieren ist Velox-KAi vorbehalten.',

  'verify.extracted': 'Gelesener Wert',
  'verify.sourceTitle': 'Quelle',
  'verify.page': 'Seite {page}',
  'verify.pageUnknown': 'Seite unbekannt',
  'verify.openSource': 'Seite im PDF öffnen',
  'verify.noSourceLink':
    'Das Quell-PDF wird für dieses Pack nicht ausgeliefert. Prüfen Sie den Auszug gegen Ihre eigene Kopie.',
  'verify.noCitation':
    'Zu diesem Eintrag gibt es keinen Quellenauszug. Ohne Quellenangabe lässt er sich hier nicht bestätigen.',
  'verify.sameSource': 'Gleiche Stelle wie oben.',
  'verify.expand': 'Zeile öffnen und gegen die Quelle prüfen',
  'verify.collapse': 'Zeile schliessen',
  'verify.zoneRowItem': 'Zonenzeile selbst',
  'verify.zoneRowItemHint':
    'Bestätigt, dass diese Zone so in der BZO steht. Erst damit verlässt die Gemeinde den Entwurfsmodus.',
  'verify.disposition': 'Abgleich mit der Quelle',
  'verify.evidenceFor': 'Beleg für',
  'verify.confirm': 'stimmt',
  'verify.correct': 'korrigieren',
  'verify.unreadable': 'nicht lesbar',
  'verify.unreadableHint': 'Wird als fehlender Eingabewert geführt — nicht als 0.',
  'verify.newValue': 'Wert laut BZO',
  'verify.newValuePlaceholder': 'Wert eintragen',
  'verify.notRead': 'nicht gelesen',
  // Ziffern (AZ, GFZ) stehen in der BZO als Prozentangabe oder als Bruch. Beides
  // ist richtig; die Zelle zeigt darum beides, statt still umzurechnen.
  'verify.ratioBoth': 'Im Dokument {printed} — normalisiert {normalised}',
  'verify.ratioOpen':
    'Schreibweise offen: {printed} ist als Prozentangabe und als Bruch lesbar. Bitte den Wert so eintragen, wie er im Dokument steht.',
  'verify.readAs': 'Im Dokument als {term}',
  'verify.markAllConfirmed': 'Ganze Zeile als «stimmt» markieren',
  'verify.markAllHint':
    'Setzt jeden Eintrag dieser Zeile auf «stimmt». Sie können danach einzelne Einträge weiterhin korrigieren.',
  'verify.submitRow': '{count} Einträge übernehmen',
  'verify.submitRowOne': '1 Eintrag übernehmen',
  'verify.submitRowNone': 'Noch nichts entschieden',
  'verify.submitting': 'Wird übernommen …',
  'verify.rowSaved': 'Übernommen und mit Quellenangabe protokolliert.',
  'verify.rowError': 'Die Übernahme ist fehlgeschlagen',
  'verify.correctionNeedsValue': 'Bitte den Wert laut BZO eintragen.',
  'verify.status.verified': 'verifiziert',
  'verify.status.unverified': 'offen',
  'verify.status.placeholder': 'Platzhalter',
  'verify.progress': '{verified} von {total} verifiziert',
  'verify.ruleKind': 'Regeltyp',
  'verify.ruleKindUnknown': 'noch nicht bestimmt',
  'verify.ruleCheckTitle': 'Vergleich',
  'verify.ruleNoCheck':
    'Der Entwurf enthält keinen Vergleich — das ist Absicht, nicht eine Lücke.',
  'verify.ruleOp': 'Operator',
  'verify.ruleValue': 'Grenzwert',
  'verify.ruleValuePlaceholder': 'Zahl aus der zitierten Stelle',
  'verify.ruleArtifact': 'Pflichtunterlage',
  'verify.ruleReject': 'verwerfen',
  'verify.ruleRejectHint': 'Die Extraktion hat hier etwas gefunden, was keine Regel ist.',
  'verify.ruleSubmit': 'Regel übernehmen',
  'verify.ruleNeedsCheck': 'Bitte Operator und Grenzwert aus der Quelle eintragen.',

  // ── M5: Aufbewahrung, Löschen, Verlauf und die Founder-Übersicht ──────
  'retention.title': 'Aufbewahrung',
  'retention.window': 'Aufbewahrung: {days} Tage',
  'retention.note': 'Nach Ablauf der Frist löscht der Baueingabe-Check die hochgeladenen Dateien, die extrahierten Fakten und alle Prüfläufe samt Berichten automatisch und endgültig. Im Audit-Trail bleibt festgehalten, dass gelöscht wurde — die gelöschten Werte selbst bleiben nicht.',
  'retention.deleteOn': 'Löschung am {date}',
  'retention.deleteIn': 'Löschung in {days} Tagen (am {date})',
  'retention.deleteToday': 'Löschung heute',
  'retention.due': 'Frist abgelaufen — wird beim nächsten Löschlauf entfernt',
  'retention.none': 'Keine Frist hinterlegt',
  'delete.cta': 'Jetzt löschen',
  'delete.title': 'Dieses Dossier endgültig löschen?',
  'delete.what': 'Gelöscht werden: die hochgeladenen PDFs auf dem Server, die extrahierten Fakten, alle Prüfläufe und deren Berichte.',
  'delete.irreversible': 'Das lässt sich nicht rückgängig machen.',
  'delete.keeps': 'Im Audit-Trail bleibt stehen, dass gelöscht wurde — wer, wann und wie viel. Die gelöschten Angaben selbst bleiben nicht.',
  'delete.confirm': 'Endgültig löschen',
  'delete.cancel': 'Abbrechen',
  'delete.busy': 'Wird gelöscht …',
  'delete.done': 'Gelöscht: {files} Datei(en), {runs} Prüflauf/Prüfläufe. {redacted} Audit-Einträge bleiben ohne Inhalt bestehen.',
  'dossierAudit.cta': 'Verlauf',
  'dossierAudit.title': 'Verlauf dieses Dossiers',
  'dossierAudit.lead': 'Uploads, Prüfläufe, Faktenkorrekturen, Exporte und Änderungen am Datenmodus — wer, wann, was.',
  'dossierAudit.back': 'Zurück zum Workspace',
  'audit.when': 'Zeitpunkt',
  'audit.who': 'Wer',
  'audit.what': 'Vorgang',
  'audit.detail': 'Angaben',
  'audit.empty': 'Noch keine Ereignisse aufgezeichnet.',
  'audit.system': 'System',
  'audit.redacted': 'Inhalt nach Ablauf der Aufbewahrung entfernt',
  'audit.action.auth.sign_in': 'Anmeldung',
  'audit.action.auth.sign_out': 'Abmeldung',
  'audit.action.invite.created': 'Einladung erstellt',
  'audit.action.invite.revoked': 'Einladung widerrufen',
  'audit.action.invite.accepted': 'Einladung eingelöst',
  'audit.action.workspace.created': 'Workspace angelegt',
  'audit.action.workspace.data_mode_changed': 'Datenmodus geändert',
  'audit.action.workspace.deleted': 'Workspace gelöscht',
  'audit.action.dossier.uploaded': 'Dossier hochgeladen',
  'audit.action.dossier.fact_edited': 'Fakten korrigiert',
  'audit.action.dossier.deleted': 'Dossier gelöscht',
  'audit.action.run.started': 'Prüflauf gestartet',
  'audit.action.run.finished': 'Prüflauf beendet',
  'audit.action.report.exported': 'Bericht exportiert',
  'audit.action.rulebase.bzo_ingested': 'BZO erfasst',
  'audit.action.rulebase.zone_verified': 'Zonenzeile verifiziert',
  'audit.action.rulebase.rule_verified': 'Regel verifiziert',
  'audit.action.rulebase.pack_exported': 'Pack exportiert',
  'audit.action.retention.swept': 'Löschlauf ausgeführt',
  'nav.admin': 'Verwaltung',
  'admin.title': 'Founder-Übersicht',
  'admin.lead': 'Alle Workspaces, alle Läufe, die Token-Kosten und die offene Verifikationsarbeit. Diese Ansicht ist Velox-intern.',
  'admin.forbidden': 'Diese Ansicht ist der Rolle Founder vorbehalten.',
  'admin.workspacesTitle': 'Workspaces',
  'admin.workspacesCaption': 'Ein Büro pro Zeile, mit Zählern — ohne Inhalte aus den Dossiers.',
  'admin.col.workspace': 'Workspace',
  'admin.col.dataMode': 'Datenmodus',
  'admin.col.members': 'Mitglieder',
  'admin.col.dossiers': 'Dossiers',
  'admin.col.runs': 'Läufe',
  'admin.col.failed': 'davon fehlgeschlagen',
  'admin.col.files': 'Dateien',
  'admin.col.storage': 'Belegt',
  'admin.col.drafts': 'Entwurfs-Packs',
  'admin.col.lastRun': 'Letzter Lauf',
  'admin.col.tokens': 'Token (ein/aus)',
  'admin.col.tokensDay': 'Token 24 h / Tagesdeckel',
  'admin.col.retention': 'Aufbewahrung',
  'admin.col.due': 'fällig',
  'admin.col.status': 'Status',
  'admin.col.mode': 'Art',
  'admin.col.duration': 'Dauer',
  'admin.col.started': 'Gestartet',
  'admin.col.gemeinde': 'Gemeinde',
  'admin.col.open': 'Offen',
  'admin.totals': 'Summe',
  'admin.dayBudgetReached': 'Tagesdeckel erreicht',
  'admin.dayBudgetLead': 'Der Tagesdeckel gilt pro Workspace über rollende 24 Stunden (BAUPILOT_TOKEN_BUDGET_PER_WORKSPACE_DAY). Ein Strich heisst: kein Deckel gesetzt. Die Summenzeile hat keinen eigenen Deckel.',
  'admin.runsTitle': 'Läufe über alle Workspaces',
  'admin.runsCaption': 'Die letzten Prüfläufe mit ihren Token-Kosten. Die Kostenzahl ist intern.',
  'admin.runsEmpty': 'Noch kein Prüflauf.',
  'admin.openRun': 'Lauf ansehen',
  'admin.queueTitle': 'Verifikations-Warteschlange',
  'admin.queueLead': 'Entwurfs-Packs aus allen Workspaces, die noch niemand gegen die Quelle bestätigt hat. Bis dahin laufen sie im indikativen Modus.',
  'admin.queueEmpty': 'Kein Entwurfs-Pack wartet auf Verifikation.',
  'admin.queueOpen': '{zones} Zonenzeile(n), {rules} Regel(n) offen',
  'admin.queueRefused': 'Am Daten-Gate abgewiesen — dieser Entwurf wird nicht verwendet.',
  'admin.queueVerify': 'Verifizieren',
  'admin.systemTitle': 'Systemweite Ereignisse',
  'admin.systemLead': 'Ereignisse ohne Workspace: Founder-Anmeldungen und Löschläufe.',
  'admin.retentionTitle': 'Aufbewahrung und Löschlauf',
  'admin.sweepEvery': 'Der Löschlauf läuft in diesem Prozess alle {minutes} Minuten.',
  'admin.sweepOff': 'Der automatische Löschlauf ist ausgeschaltet (Intervall 0). Es löscht nur, wer hier auf «Jetzt löschen» drückt oder den Lauf per Cron startet.',
  'admin.lastSweep': 'Letzter Lauf in diesem Prozess: {when} — {count} Dossier(s) gelöscht.',
  'admin.neverSwept': 'In diesem Prozess wurde noch nicht gefegt.',
  'admin.dueNow': '{count} Dossier(s) sind fällig.',
  'admin.dueNone': 'Zurzeit ist kein Dossier fällig.',
  'admin.sweepDry': 'Nur zählen',
  'admin.sweepRun': 'Löschlauf jetzt ausführen',
  'admin.sweepBusy': 'Der Löschlauf läuft …',
  'admin.sweepDone': 'Löschlauf beendet: {dossiers} Dossier(s), {files} Datei(en), {runs} Prüflauf/Prüfläufe gelöscht.',
  'admin.sweepDryDone': 'Zählung: {due} Dossier(s) wären jetzt fällig. Es wurde nichts gelöscht.',
  'admin.deleteWorkspaceTitle': 'Workspace löschen',
  'admin.deleteWorkspaceWhat': 'Gelöscht werden alle Dossiers samt Dateien, alle Prüfläufe und Berichte, die Entwurfs-Packs, die Einladungen, die Mitgliedschaften und der Audit-Trail dieses Workspace.',
  'admin.deleteWorkspaceKeeps': 'Bestehen bleibt ein einziger Eintrag ohne Workspace-Bezug: dass dieser Workspace gelöscht wurde, wann und wie viel dabei entfernt wurde.',
  'admin.deleteWorkspaceLabel': 'Zum Bestätigen den Kurznamen «{slug}» eintippen',
  'admin.deleteWorkspaceCta': 'Workspace endgültig löschen',
  'admin.deleteWorkspaceMismatch': 'Der eingetippte Kurzname stimmt nicht — es wurde nichts gelöscht.',
  'admin.deleteWorkspaceDone': 'Der Workspace wurde gelöscht: {dossiers} Dossier(s), {files} Datei(en), {runs} Prüflauf/Prüfläufe, {audit} Audit-Einträge.',
  'admin.selectWorkspace': 'Workspace wählen',

  'notFound.title': 'Seite nicht gefunden',
  'notFound.lead': 'Diese Adresse gehört zu keiner Ansicht von Baueingabe-Check-KAi.',
  'notFound.home': 'Zur Übersicht',
} as const

export type TranslationKey = keyof typeof de
